import { Box, Dialog, Typography } from '@mui/material';
import { ChevronLeft, Upload, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
    DiskPickerInputs,
    FileImportStrip,
    ImportAllDone,
    ImportDropzone,
    ImportFooter,
    ImportPatientReview,
    ImportProgressPanel,
    ImportQueueRail,
    PacsSourcePanel,
} from './components';
import { useDiskImport, usePacsImport } from './hooks';
import {
    arrivedWhole,
    formatImportMessage,
    isDiskIdle,
    rejectSummary,
} from './model';
import { QuietButton } from '../shared';

const noop = () => undefined;

/**
 * Takes a drop the dialog will not act on, so it neither reaches the browser default, which
 * navigates away from the application, nor replaces a screen the user is in the middle of.
 */
const refuseDrop = {
    onDragOver: (event: React.DragEvent) => event.preventDefault(),
    onDrop: (event: React.DragEvent) => event.preventDefault(),
};

type ImportWizardProps = {
    open: boolean;
    onClose: () => void;
    /**
     * A retrieval whose every series arrived, naming the connector patient they landed in. The
     * dialog has already dismissed itself by the time this runs; where the user goes next is the
     * screen's decision, not a dialog's, so this reports rather than navigates.
     */
    onRetrieved?: (patientId: string) => void;
};

/**
 * The DICOM import dialog: one place to bring a patient in, from the configured DICOM sources or
 * from files on this computer.
 *
 * It opens on the search, which is where a clinical import usually starts. Files dropped anywhere
 * on the dialog, or picked, replace the search with the review of what was read, and the search
 * comes back once that review holds nothing left to lose. Which of the two is on screen is read off
 * the file import's own state rather than kept beside it, so the dialog cannot show the search while
 * a file review is under way, nor the reverse.
 */
export default function ImportWizard({
    open,
    onClose,
    onRetrieved,
}: ImportWizardProps) {
    const intl = useIntl();
    const disk = useDiskImport(open);
    const pacs = usePacsImport(open);
    const { stage, queue, entry, committing, state, actions } = disk;
    const diskActive = !isDiskIdle(state);
    // A drop replaces the search, so it is taken only while the search has nothing on screen that
    // it would take away: a picked patient, a retrieval being set up, or its report.
    const acceptsDrop = diskActive || pacs.state.stage.kind === 'search';
    // Leaving the file review discards it, so the way back is offered only once nothing in it is
    // left to decide: nothing read, nothing readable, or every patient settled.
    const canLeaveFiles =
        stage.kind === 'awaiting-drop' ||
        stage.kind === 'no-dicom' ||
        stage.kind === 'done';

    const close = () => {
        actions.restart();
        pacs.actions.reset();
        onClose();
    };

    // Dismissal is refused while either source has work in flight whose terminal report has not
    // been shown: a close mints a new generation, so the disk commit's answer or the retrieval
    // loop's refusal tally would be dropped unseen while the requests run on headless.
    const closeBlocked = committing || pacs.isRequesting;

    // The retrieval a handoff has already been decided for. The stage object is rebuilt by every
    // observation, and a generation is never reused, so this holds to one decision per retrieval
    // however often the effect re-runs once a stage has settled.
    const handedOffRef = useRef<number | undefined>(undefined);

    /**
     * A retrieval that arrived whole dismisses the dialog and hands the patient to the screen.
     *
     * Only on a whole arrival: a series that failed, that nothing arrived for, or that the source
     * refused outright is the outcome the reader must be shown, so any of them holds the report
     * open rather than being navigated away from. Only while the dialog is still open, because
     * dismissing it is how a user says they do not want to be taken anywhere, and the transfer
     * completing later must not then yank them out of whatever they moved on to. Only while the
     * disk source is idle, because the dismissal restarts the disk machine: a queue, an upload in
     * flight or its report would go with it unread. The retrieval keeps its own report on the DICOM
     * pane either way, so a declined handoff withholds nothing.
     */
    useEffect(() => {
        if (!open) {
            return;
        }
        const pacsStage = pacs.state.stage;
        if (pacsStage.kind !== 'arrived' || !pacsStage.patientId) {
            return;
        }
        if (handedOffRef.current === pacs.state.generation) {
            return;
        }
        if (!arrivedWhole(pacsStage)) {
            return;
        }
        // Latched before the gate below: a handoff this retrieval does not get is not owed later,
        // when the dismissal would land in the middle of whatever the user has moved on to.
        handedOffRef.current = pacs.state.generation;
        if (!isDiskIdle(state)) {
            return;
        }
        close();
        onRetrieved?.(pacsStage.patientId);
    });

    // Scan rejects belong to the drop, so they are reported once on its own row.
    const rejects = queue?.rejects ?? [];
    const skippedInDrop =
        rejects.length === 0
            ? undefined
            : {
                  count: rejects.length,
                  summary: rejectSummary(intl, rejects) ?? '',
              };

    return (
        <Dialog
            open={open}
            onClose={closeBlocked ? noop : close}
            maxWidth="md"
            fullWidth
            aria-labelledby="import-wizard-title"
            PaperProps={{
                elevation: 0,
                // On the Paper, not a pane: files may be dropped anywhere on the dialog.
                ...(acceptsDrop ? disk.dropHandlers : refuseDrop),
                sx: {
                    position: 'relative',
                    border: 1,
                    borderColor:
                        acceptsDrop && disk.isDragging
                            ? 'primary.main'
                            : 'divider',
                    bgcolor: 'background.default',
                    height: 'min(80vh, 640px)',
                },
            }}
        >
            <DiskPickerInputs picker={disk.picker} />
            {!diskActive && acceptsDrop && disk.isDragging && <DropOverlay />}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.5,
                    px: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                }}
            >
                {diskActive && canLeaveFiles && (
                    <QuietButton onClick={actions.restart}>
                        <ChevronLeft size={13} />
                        {intl.formatMessage({
                            id: 'patientList.import.backToSearch',
                        })}
                    </QuietButton>
                )}
                <Typography
                    id="import-wizard-title"
                    component="h2"
                    sx={{ fontSize: 15, fontWeight: 500, flex: 1 }}
                >
                    {intl.formatMessage({ id: 'patientList.import.title' })}
                </Typography>
                <QuietButton
                    onClick={close}
                    disabled={closeBlocked}
                    aria-label={intl.formatMessage({
                        id: 'patientList.import.close',
                    })}
                >
                    <X size={13} />
                </QuietButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                {diskActive && (
                    <ImportQueueRail
                        diskSubtitle={disk.subtitle}
                        skipped={skippedInDrop}
                        queue={
                            queue && {
                                entries: queue.entries,
                                // The summary marks no row current; selecting one only acts while reviewing.
                                current:
                                    stage.kind === 'done'
                                        ? undefined
                                        : queue.current,
                                onSelect:
                                    stage.kind === 'reviewing'
                                        ? actions.selectEntry
                                        : undefined,
                            }
                        }
                    />
                )}

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {diskActive ? (
                        <>
                            {state.refusal !== undefined &&
                                stage.kind !== 'awaiting-drop' && (
                                    <Typography
                                        role="alert"
                                        sx={{
                                            flex: 'none',
                                            py: 1,
                                            px: 2.5,
                                            fontSize: 12,
                                            color: 'error.main',
                                            borderBottom: 1,
                                            borderColor: 'divider',
                                        }}
                                    >
                                        {formatImportMessage(
                                            intl,
                                            state.refusal
                                        )}
                                    </Typography>
                                )}

                            {stage.kind === 'awaiting-drop' && (
                                <ImportDropzone
                                    isDragging={disk.isDragging}
                                    refusal={
                                        state.refusal &&
                                        formatImportMessage(intl, state.refusal)
                                    }
                                    picker={disk.picker}
                                />
                            )}

                            {stage.kind === 'expanding' && (
                                // A running count with no total: an archive's file count is unknown until it is opened.
                                <ImportProgressPanel
                                    label={intl.formatMessage(
                                        {
                                            id: 'patientList.import.progress.expanding',
                                        },
                                        { count: stage.produced }
                                    )}
                                />
                            )}

                            {stage.kind === 'scanning' && (
                                <ImportProgressPanel
                                    label={intl.formatMessage(
                                        {
                                            id: 'patientList.import.progress.scanning',
                                        },
                                        {
                                            scanned: stage.scanned,
                                            total: stage.total,
                                        }
                                    )}
                                />
                            )}

                            {stage.kind === 'no-dicom' && (
                                <Box sx={{ p: 3, fontSize: 13 }} role="alert">
                                    <Typography sx={{ fontSize: 13, mb: 1 }}>
                                        {intl.formatMessage({
                                            id: 'patientList.import.noDicom.title',
                                        })}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 12,
                                            color: 'text.secondary',
                                            mb: 1,
                                        }}
                                    >
                                        {rejectSummary(intl, stage.rejects)}
                                    </Typography>
                                    <QuietButton onClick={actions.restart}>
                                        {intl.formatMessage({
                                            id: 'patientList.import.noDicom.chooseDifferent',
                                        })}
                                    </QuietButton>
                                </Box>
                            )}

                            {stage.kind === 'done' && queue !== undefined && (
                                <ImportAllDone entries={queue.entries} />
                            )}

                            {disk.reviewOpen &&
                                entry !== undefined &&
                                queue !== undefined && (
                                    <ImportPatientReview
                                        drop={entry.drop}
                                        verdict={entry.verdict}
                                        status={entry.status}
                                        // The queue cannot move mid-commit, so the in-flight request is always this entry's own.
                                        inFlight={disk.inFlight}
                                        landed={entry.landed}
                                        landingUnknown={entry.landingUnknown}
                                        onRetryCheck={actions.recheck}
                                        onUndoSkip={actions.undoSkip}
                                        checked={entry.selectedSeries}
                                        onToggle={actions.toggleSeries}
                                        alreadyStored={entry.alreadyStored}
                                        notes={entry.notes}
                                        onContextChange={actions.changeContext}
                                    />
                                )}
                        </>
                    ) : (
                        <>
                            <PacsSourcePanel pacs={pacs} />
                            {acceptsDrop && (
                                <FileImportStrip picker={disk.picker} />
                            )}
                        </>
                    )}
                </Box>
            </Box>

            <ImportFooter
                disk={disk}
                onClose={close}
                closeDisabled={closeBlocked}
                showDiskActions={diskActive}
            />
        </Dialog>
    );
}

/** Shown over the search while files are dragged across it: letting go switches to the file review. */
function DropOverlay() {
    const intl = useIntl();
    return (
        <Box
            aria-hidden
            sx={{
                position: 'absolute',
                inset: 8,
                zIndex: 2,
                pointerEvents: 'none',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                border: 2,
                borderStyle: 'dashed',
                borderColor: 'primary.main',
                borderRadius: 1,
                bgcolor: 'background.default',
                opacity: 0.94,
                color: 'primary.main',
            }}
        >
            <Upload size={26} />
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {intl.formatMessage({ id: 'patientList.import.dropToImport' })}
            </Typography>
        </Box>
    );
}
